import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import 'firebase/compat/messaging';
import { SubSink } from './core/subsink';

import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { SettingsService } from './core/services/settings/settings.service';
import { BadisOauthLoginService } from './core/services/auth/badis-oauth-login.service';
import { SettingsModel } from './core/services/settings/dtos/settings-model';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { fuseAnimations } from '@fuse/animations';
import { LoggerMonitor } from './shared/services/logger-monitor.service';


import { DeviceHubConnectionService } from './core/services/hubs/device-hub-connection.service';
import { PropertyHubConnectionService } from './core/services/hubs/property-hub-connection.service';
import { LocationHubConnectionService } from './core/services/hubs/location-hub-connection.service';
import { SystemHubConnectionService } from './core/services/hubs/system-hub-connection.service';
import { AlertHubConnectionService } from './core/services/hubs/alert-hub-connection.service';
import { PostOfficeService } from './core/services/post-office/post-office.service';
import { LanguageService } from './shared/services/language.service';
import { LiveDTO } from './modules/live/dtos/live';
import { NotificationService } from './pages/notifications/services/notification.service';
import { Title } from '@angular/platform-browser';
import { UpdateCheckService } from './shared/services/update-check.service';
import { BackgroundService } from '@app/shared/components/background/background.service';
import { GlobalVariablesService } from './core/services/global-variables.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import dayjs from '@shared/date/dayjs-bootstrap';
import { MaintenanceHubConnectionService } from './core/services/hubs/maintenance-hub-connection.service';
import { ChatHubConnectionService } from './core/services/hubs/chat-hub-connection.service';
import { ChatStateService } from './core/services/chat/chat-state.service';
import { Maintenance } from './core/services/hubs/models/maintenance.model';
import { ServiceName, ServiceReadyStateService } from './core/services/service-ready-state.service';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { Observable, combineLatest, map, shareReplay, startWith } from 'rxjs';
import { ToastNotifyService } from './pages/notifications/services/toast-notify.service';
import { NorthernColors } from './shared/components/northern-lights/models';
import { MediaUnlockService } from './shared/services/media-unlock.service';
import { ScreenSaverService } from './core/services/screen-saver.service';
import { SystemMenuService } from './core/services/system-menu.services';
import { environment } from '@env/environment';
import { ExternalLoaderBridgeService } from './core/services/network-progress/external-loader-bridge.service';
import { LegacyLeafletScriptLoaderService } from './core/services/legacy-leaflet-script-loader.service';
import { DeferredStylesheetLoaderService } from './core/services/deferred-stylesheet-loader.service';
import { JsonLdService } from './core/services/json-ld.service';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(ToastContainerDirective, { static: true }) toastContainer!: ToastContainerDirective;
    private subs = new SubSink();
    private splashRevealDone = false;
    private legacyLeafletScriptsRequested = false;

    enableAlerts: boolean = false;
    newUpdateAvailable: boolean;
    enablePushAlerts: boolean = false;
    private chatFeatureEnabled = false;
    private chatFeatureInitialized = false;

    muteCountdown$: Observable<number>;
    muteCountdownLabel$: Observable<string | null>;
    NorthernColors = NorthernColors;

    // Toast container hover state
    isToastContainerHovered: boolean = false;
    constructor(
        public library: FaIconLibrary,
        private router: Router,
        private splash: FuseSplashScreenService,
        private loginService: BadisOauthLoginService,
        private settingService: SettingsService,
        logger: NGXLogger,

        private deviceHub: DeviceHubConnectionService,
        private propertyHub: PropertyHubConnectionService,
        private locationHub: LocationHubConnectionService,
        private systemHub: SystemHubConnectionService,
        private alertHub: AlertHubConnectionService,
        private maintenanceHub: MaintenanceHubConnectionService,
        private chatHub: ChatHubConnectionService,
        private chatState: ChatStateService,
        public backgroundService: BackgroundService,
        private postOfficeService: PostOfficeService,
        languageService: LanguageService,
        public updateCheckService: UpdateCheckService,
        private titleService: Title,
        public toastNotify: ToastNotifyService,
        public notificationService: NotificationService,
        global: GlobalVariablesService,
        private readyStateService: ServiceReadyStateService,
        public toast: ToastrService,
        private mediaUnlock: MediaUnlockService,
        public screenSaver: ScreenSaverService,
        private systemMenuService: SystemMenuService,
        private externalLoaderBridge: ExternalLoaderBridgeService,
        private legacyLeafletScriptLoader: LegacyLeafletScriptLoaderService,
        private deferredStylesheetLoader: DeferredStylesheetLoaderService,
        private jsonLd: JsonLdService
    ) {
        languageService.init();

        this.splash.show();
        this.deferredStylesheetLoader.ensureForPath(window.location.pathname || '/');

        this.unreadCount$ = this.notificationService.getNotificationsData().pipe(
            map(data => Number.isFinite(data?.unreadMessages) ? Math.max(0, data.unreadMessages) : 0),
            startWith(0),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.activeToastCount$ = this.toastNotify.getActiveNotifications().pipe(
            map(count => Number.isFinite(count) ? Math.max(0, count) : 0),
            startWith(this.toastNotify.activeNotifications ?? 0),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.queuedMessageCount$ = this.toastNotify.getQueuedMessageCount().pipe(
            map(count => Number.isFinite(count) ? Math.max(0, count) : 0),
            startWith(this.toastNotify.queuedMessageCount ?? 0),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.muteCountdown$ = this.toastNotify.getCountdown();
        this.muteCountdownLabel$ = this.muteCountdown$.pipe(
            map((seconds) => {
                if (!Number.isFinite(seconds) || seconds <= 0) {
                    return null;
                }

                const remainingMinutes = Math.max(1, Math.ceil(seconds / 60));
                return `${remainingMinutes}m`;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        combineLatest([this.unreadCount$, this.activeToastCount$])
            .pipe(untilDestroyed(this))
            .subscribe(([unreadCount, toastCount]) => {
                this.lastUnreadCount = unreadCount;
                this.lastToastCount = toastCount;
                this.updateTitle(unreadCount, toastCount);
            });

        logger.registerMonitor(new LoggerMonitor());

        void this.loadFontAwesomeIconPacks();


        this.loginService.onAuthStatusChanged.subscribe(async (isAuthenticated: boolean) => {

            if (isAuthenticated && this.loginService.isUserLoggedIn()) {
                await this.pageIsReady();
                this.systemMenuService.populateSystemMenu();

            } else {
                this.notificationService.endAuthenticatedSession();

                //redirect to login page with return url
                //this.router.navigate(['/auth', { returnUrl: this.router.url }]);
            }
            this.settingService.refresh();
        });

        this.settingService.settingChanged.subscribe((setting: SettingsModel.Settings) => {
            switch (setting.primaryKey) {
                case 'Performance.Browser.EnableDebug':
                    const enableDebug = setting.userSetting.primaryValue;

                    console.debug('debug mode:', enableDebug ? 'true' : 'false');
                    const config = logger.getConfigSnapshot();
                    config.level = enableDebug ? NgxLoggerLevel.TRACE : NgxLoggerLevel.ERROR;
                    config.serverLogLevel = NgxLoggerLevel.ERROR;
                    config.disableConsoleLogging = !enableDebug;
                    logger.updateConfig(config);
                    global.isDebugMode = enableDebug;
                    break;
                case 'General.Region.TimeZone':
                    const timeZone = setting.userSetting.primaryValue || 'Europe/Istanbul';
                    dayjs.tz.setDefault(timeZone);
                    break;
                case 'General.Tracker.SignalR.Address':
                    global.setTrackerAddress(setting.userSetting?.primaryValue ?? setting.primaryValue);
                    break;
                case 'Notification.Alerts.Enable':
                    this.enableAlerts = setting.userSetting.getValue<boolean>();
                    break;
                case 'Chat.Enabled':
                    this.chatFeatureEnabled = this.resolveBooleanSetting(setting);
                    this.applyChatFeatureState();
                    break;


                default:
                    break;
            }


            if (setting.changeRequest === LiveDTO.ChangeRequest.User) {

                switch (setting.primaryKey) {


                }
            }


        });





    }

    private async loadFontAwesomeIconPacks(): Promise<void> {
        const [regularIcons, lightIcons, solidIcons] = await Promise.all([
            import('@fortawesome/pro-regular-svg-icons'),
            import('@fortawesome/pro-light-svg-icons'),
            import('@fortawesome/pro-solid-svg-icons')
        ]);

        this.library.addIconPacks(regularIcons.far, lightIcons.fal, solidIcons.fas);
    }

    async pageIsReady() {
        this.postOfficeService.init();

        await this.notificationService.initializeAuthenticatedSession();
        this.notificationService.handleTokenRefresh();

        this.deviceHub.init();
        this.propertyHub.init();
        this.locationHub.init();
        this.systemHub.init();
        this.alertHub.init();
        this.maintenanceHub.init();

        this.readyStateService.setServiceReady(ServiceName.SignalR, false);
        this.startHubConnectionsInBackground();




        this.alertHub.alertUpdated$
            .pipe(untilDestroyed(this))
            .subscribe((alert) => {
                if (!alert || !this.enableAlerts || this.enablePushAlerts)
                    return;

                this.toastNotify.showAlert(alert);
            });

        this.maintenanceHub.fragmentationProgress$.subscribe((data) => {
            if (!data)
                return;

            if (data.status == Maintenance.MaintenanceStatus.Started)
                this.toastNotify.showMaintenanceAlert(data);
        });

        this.screenSaver.start(60);

    }




    ngOnInit(): void {
        this.unregisterServiceWorkersForNonProd();
        this.monitorBootSplashLifecycle();

        // todo: mesut -> if something goes wrong! delete me
        // this.screenLock.startWatching(5).subscribe((isTimedOut: boolean) => {
        //     if (isTimedOut) {
        //      //   console.log('screen locked! just kidding:) not today but soon');
        //     }
        // });

        // Reset title to default on every route change
        // BaseComponent pages will override this in ngAfterViewInit
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event) => {
            const navigation = event as NavigationEnd;
            const currentUrl = navigation.urlAfterRedirects || this.router.url;
            this.updateTitle(this.lastUnreadCount, this.lastToastCount);
            this.updateBackgroundVisibility(currentUrl);
            this.ensureLegacyLeafletScriptsForRoute(currentUrl);
            this.deferredStylesheetLoader.ensureForPath(currentUrl);
            this.jsonLd.scheduleUpdate(currentUrl);
        });

        this.updateBackgroundVisibility(this.router.url);
        this.ensureLegacyLeafletScriptsForRoute(this.router.url);
        this.deferredStylesheetLoader.ensureForPath(this.router.url);
        this.jsonLd.scheduleUpdate(this.router.url);
    }

    private ensureLegacyLeafletScriptsForRoute(url: string): void {
        if (this.legacyLeafletScriptsRequested || this.legacyLeafletScriptLoader.isLoaded()) {
            return;
        }

        if (!this.legacyLeafletScriptLoader.shouldLoadForPath(url || '/')) {
            return;
        }

        this.legacyLeafletScriptsRequested = true;
        void this.legacyLeafletScriptLoader.ensureLoaded()
            .catch((error) => {
                this.legacyLeafletScriptsRequested = false;
                if (!environment.production) {
                    console.warn('[boot] legacy leaflet scripts failed to load', error);
                }
            });
    }

    private monitorBootSplashLifecycle(): void {
        this.externalLoaderBridge.snapshot$
            .pipe(untilDestroyed(this))
            .subscribe((snapshot) => {
                if (
                    this.splashRevealDone ||
                    !snapshot ||
                    snapshot.state !== 'done' ||
                    !snapshot.angularReady
                ) {
                    return;
                }

                this.splashRevealDone = true;
                this.splash.hide();

                if (!environment.production) {
                    console.debug('[boot] splash hidden: loader done + angular ready');
                }
            });
    }

    private startHubConnectionsInBackground(): void {
        const hubs = [
            { name: 'deviceHub', connect: () => this.deviceHub.startConnection() },
            { name: 'propertyHub', connect: () => this.propertyHub.startConnection() },
            { name: 'locationHub', connect: () => this.locationHub.startConnection() },
            { name: 'systemHub', connect: () => this.systemHub.startConnection() },
            { name: 'alertHub', connect: () => this.alertHub.startConnection() },
            { name: 'maintenanceHub', connect: () => this.maintenanceHub.startConnection() },
            { name: 'chatHub', connect: () => this.chatHub.startConnection() }
        ];

        const startedAt = Date.now();
        const connectionPromises = hubs.map((hub) => this.connectHubWithRetry(hub.name, hub.connect));

        void Promise.allSettled(connectionPromises)
            .then((results) => {
                const successCount = results.reduce((count, result) => {
                    if (result.status === 'fulfilled' && result.value) {
                        return count + 1;
                    }
                    return count;
                }, 0);

                this.readyStateService.setServiceReady(ServiceName.SignalR, successCount > 0);

                if (!environment.production) {
                    const elapsedMs = Date.now() - startedAt;
                    console.debug(`[boot] hub connections settled: ${successCount}/${hubs.length} in ${elapsedMs}ms`);
                }
            })
            .catch((error) => {
                this.readyStateService.setServiceReady(ServiceName.SignalR, false);
                console.warn('[boot] hub connection orchestration failed', error);
            });
    }

    private async connectHubWithRetry(name: string, connect: () => Promise<void>): Promise<boolean> {
        try {
            await connect();
            return true;
        } catch (error) {
            console.warn(`[boot] ${name} initial connection failed, retrying once`, error);
        }

        await this.delay(2000);

        try {
            await connect();
            return true;
        } catch (retryError) {
            console.warn(`[boot] ${name} retry failed`, retryError);
            return false;
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(resolve, Math.max(0, ms));
        });
    }

    private applyChatFeatureState(): void {
        if (!this.chatFeatureEnabled) {
            this.chatState.setFeatureEnabled(false);
            if (this.chatFeatureInitialized) {
                this.chatHub.disconnect();
            }
            return;
        }

        if (!this.chatFeatureInitialized) {
            this.chatHub.init();
            this.chatState.init();
            this.chatFeatureInitialized = true;
        }

        this.chatState.setFeatureEnabled(true);
        void this.chatHub.startConnection();
    }

    private resolveBooleanSetting(setting: SettingsModel.Settings | null | undefined): boolean {
        if (!setting) {
            return false;
        }

        return !!setting.userSetting?.getValue<boolean>();
    }

    private unregisterServiceWorkersForNonProd(): void {
        if (environment.production || typeof window === 'undefined' || !('serviceWorker' in navigator)) {
            return;
        }

        navigator.serviceWorker.getRegistrations()
            .then((registrations) => Promise.all(
                registrations
                    .filter((registration) => !this.isFirebaseMessagingServiceWorker(registration))
                    .map((registration) => registration.unregister())
            ))
            .catch(() => {
                // Ignore unregister errors in non-production mode.
            });
    }

    private isFirebaseMessagingServiceWorker(registration: ServiceWorkerRegistration): boolean {
        return [
            registration.active?.scriptURL,
            registration.installing?.scriptURL,
            registration.waiting?.scriptURL
        ].some((scriptUrl) => !!scriptUrl && scriptUrl.includes('/firebase-messaging-sw.js'));
    }

    @HostListener('document:pointerdown')
    onGlobalPointerDown(): void {
        this.mediaUnlock.attemptUnlock();
    }

    public activeToastCount$: Observable<number>;
    public queuedMessageCount$: Observable<number>;
    private unreadCount$: Observable<number>;
    private lastUnreadCount: number = 0;
    private lastToastCount: number = 0;

    private updateTitle(unreadCount: number, toastCount: number): void {
        const badgeCount = unreadCount + toastCount;

        // Get current title and remove existing unread count prefix
        const currentTitle = this.titleService.getTitle();
        const titleWithoutPrefix = currentTitle.replace(/^\(\d+\)\s+/, '');

        if (badgeCount > 0) {
            this.titleService.setTitle(`(${badgeCount}) ${titleWithoutPrefix}`);
        } else {
            this.titleService.setTitle(titleWithoutPrefix);
        }
    }


    private updateBackgroundVisibility(url: string): void {
        const path = url.split('?')[0].split('#')[0];
        this.backgroundService.setVisible(!path.startsWith('/landing'));
    }

    toggleMuteNotifications() {
        this.toastNotify.toggleMute();
    }

    clearNotifications() {
        this.toastNotify.clearNotifications();
    }

    // Toast container hover event handler
    onToastContainerHover(isHovered: boolean) {
        this.isToastContainerHovered = isHovered;
    }


    ngAfterViewInit(): void {
        this.updateCheckService.checkForUpdates();
        this.toastNotify.setToastContainer(this.toastContainer);
    }


    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

}
